import React from "react";
import { withRouter } from "react-router";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import is from "is_js";
import { List, Record } from "immutable";
import ReactGA from "react-ga4";
import PropTypes from "prop-types";

import {
  Grid,
  connect,
  ApiCall,
  moment,
  PrivilegeChecker,
} from "omni-common-ui";

import FinancialClosingMoment from "domain/FinancialClosingMoment";
import Privileges from "domain/Privileges";
import {
  AttendanceStatus as Status,
  convertBackendAttendanceStatusToAttendanceStatus,
} from "data/SessionsAttendance/status";
import { actions as GroupMembers } from "data/GroupMembers";
import * as SessionsAttendance from "data/SessionsAttendance";
import UnsavedChangesTrackingPageComponent from "components/UnsavedChangesTrackingPageComponent";
import UnsavedChangesWarning from "components/UnsavedChangesWarning";
import { Student } from "./Student";
import { isActive } from "containers/Group/status";
import { actions as uiState } from "data/UiState";

const StudentRecord = Record({
  studentGeneralInfo: undefined,
  period: undefined,
  ach: undefined,
  status: undefined,
  isInProduction: undefined,
  sessionId: undefined,
  attendanceStatus: undefined,
});

const MARKING = "edit";

export class MarkAttendanceDayView extends UnsavedChangesTrackingPageComponent {
  componentWillMount() {
    if (List.isList(this.props.students)) {
      this.setState({ students: this.props.students });
    }
    this._fetchDataIfNeeded(this.props);
    super.componentWillMount(); // for unsaved changes tracking
  }

  componentWillReceiveProps(nextProps) {
    const { students } = nextProps;
    // TODO: Revisit this hack, which prevents loss of changes on nav expand/collapse
    if (
      this.props.location.pathname === nextProps.location.pathname &&
      this.props.currentSession && // attendance data is mapped
      List.isList(this.props.currentSession.data)
    ) {
      return;
    }
    this.setState({ students });
    this._fetchDataIfNeeded(nextProps);
  }

  _fetchDataIfNeeded(props) {
    if (is.not.string(props.attendanceId)) {
      return;
    }

    props.fetchSessionAttendanceIfNeeded(props.attendanceId);
  }

  _isChangesPresent() {
    // override
    return this.props.marking && this.state.students !== this.props.students; // has local changes?
  }

  _handleSaveAttendance() {
    let { students } = this.state;

    students = students.map((student) =>
      student.set("attendanceStatus", Status[student.attendanceStatus]),
    );

    this.props
      .submitSessionAttendance(this.props.attendanceId, students)
      .then(() => {
        this.props.fetchGroupMembers(this.props.groupId);
      });

    this._forcePushRoute(this.props.buildRoute(".."));
  }

  _gaChangeAttendance(student, previousAttendance, nextAttendance) {
    ReactGA.event({
      category: "Attendance",
      action: "Changed attendance",
      label: `Changed attendance from ${previousAttendance} to ${nextAttendance}`,
    });
  }

  _handleMarkPresent(i) {
    let { students } = this.state;
    this._gaChangeAttendance(
      students.get(i).id,
      students.get(i).attendanceStatus,
      Status.present,
    );
    students = students.update(i, (student) =>
      student.set("attendanceStatus", Status.present),
    );
    this.setState({ students });
  }

  _handleMarkAbsent(i) {
    let { students } = this.state;
    this._gaChangeAttendance(
      students.get(i).id,
      students.get(i).attendanceStatus,
      Status.absent,
    );
    students = students.update(i, (student) =>
      student.set("attendanceStatus", Status.absent),
    );
    this.setState({ students });
  }

  _handleMarkLate(i) {
    let { students } = this.state;
    this._gaChangeAttendance(
      students.get(i).id,
      students.get(i).attendanceStatus,
      Status.late,
    );
    students = students.update(i, (student) =>
      student.set("attendanceStatus", Status.late),
    );
    this.setState({ students });
  }

  routerWillLeave(nextLocation) {
    if (nextLocation && nextLocation.pathname.endsWith("/save")) {
      this._handleSaveAttendance();
    } else if (nextLocation && nextLocation.pathname.endsWith("/discard")) {
      this._forcePushRoute(this.props.buildRoute({ mode: "" }), {
        students: this.props.students,
      });
    } else {
      return super.routerWillLeave(nextLocation);
    }
  }

  _setSessionCourseSwitchMode() {
    this.props.toggleViewMode("day");
  }

  _renderStudents(students, marking) {
    return students.map((student, index) => (
      <Grid.Item
        key={student.studentGeneralInfo.id}
        xs={12}
        sm={6}
        md={4}
        lg={3}
      >
        <Student
          name={student.studentGeneralInfo.firstName}
          surname={student.studentGeneralInfo.lastName}
          avatarUrl={student.studentGeneralInfo.avatarUrl}
          status={student.attendanceStatus || Status.unmarked}
          productionStatus={student.status}
          isInProduction={student.isInProduction}
          gender={student.studentGeneralInfo.gender}
          localname={student.studentGeneralInfo.localName}
          studentId={student.studentGeneralInfo.id}
          groupNumber={this.props.groupNumber}
          sessionInfo={null}
          sessionId={this.props.sessionId}
          groupId={this.props.groupId}
          marking={marking}
          onMarkPresent={() => this._handleMarkPresent(index)}
          onMarkAbsent={() => this._handleMarkAbsent(index)}
          onMarkLate={() => this._handleMarkLate(index)}
        />
      </Grid.Item>
    ));
  }

  render() {
    const { students } = this.state;
    if (!is.existy(students)) {
      return null;
    }

    return (
      <div>
        <Grid outerMargin>
          <Grid.Group>
            {this._renderStudents(students, this.props.marking)}
          </Grid.Group>
        </Grid>
        <UnsavedChangesWarning
          isOpen={!!this._isNavigationWithChangesRequested()}
          isSaving={this.props.isAnyApiCallSaving}
          onSaveClicked={() => {
            this._handleSaveAttendance();
          }}
          onDiscardClicked={() => {
            this._forcePushRequestedLocation();
          }}
          onCloseClicked={() => {
            this._setSessionCourseSwitchMode();
            this._clearRequestedLocation();
          }}
          onSaved={() => {
            this._forcePushRequestedLocation();
          }}
        />
      </div>
    );
  }
}

MarkAttendanceDayView.isInMarkingRoute = (props) =>
  props.params && props.params.mode === MARKING;

MarkAttendanceDayView.propTypes = Object.assign(
  {},
  UnsavedChangesTrackingPageComponent.propTypes,
  {
    params: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    pushRoute: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    children: PropTypes.node,
    fetchGroupMembers: PropTypes.func.isRequired,
    fetchSessionAttendance: PropTypes.func.isRequired,
    submitSessionAttendance: PropTypes.func.isRequired,
    attendanceId: PropTypes.string,
    groupId: PropTypes.string.isRequired,
    currentSession: PropTypes.object,
    markable: PropTypes.bool.isRequired,
    students: PropTypes.object.isRequired,
    buildRoute: PropTypes.func.isRequired,
    isAnyApiCallSaving: PropTypes.bool.isRequired,
    groupNumber: PropTypes.string.isRequired,
  },
);

export function mapStateToProps(state, ownProps) {
  const { groupId, sessionPlanId } = ownProps.params;
  const sessionPlan = state
    .get("rootReducer")
    .get("sessionPlans")
    .get(sessionPlanId);
  const { attendanceId } = sessionPlan;
  const group = state.get("rootReducer").get("groups").get(groupId).data;

  let markable = false;

  let students = GroupMembers.getMembersByGroup(state, groupId);

  let currentSession;
  if (is.existy(attendanceId)) {
    currentSession = state
      .get("rootReducer")
      .get("sessionsAttendance")
      .get(attendanceId);
    if (currentSession && List.isList(currentSession.data)) {
      let attendance = currentSession.data;

      // this part sets default attendance statuses when entering marking mode
      // TODO: refactor this
      if (MarkAttendanceDayView.isInMarkingRoute(ownProps)) {
        attendance = attendance.map((student) =>
          student.set(
            "attendanceStatus",
            convertBackendAttendanceStatusToAttendanceStatus(
              student.attendanceStatus,
              true,
            ),
          ),
        );
      }

      students = attendance.map((student) => {
        const id = student.studentGeneralInfo.id;
        const groupMember = students.find(
          (member) => member.studentGeneralInfo.id === id,
        );
        if (is.undefined(groupMember)) {
          return student;
        }
        return new StudentRecord(
          Object.assign(student.toObject(), {
            isInProduction: groupMember.isInProduction,
            status: groupMember.status,
            studentGeneralInfo: groupMember.studentGeneralInfo,
          }),
        );
      });
      const financialClosingMoment = FinancialClosingMoment.nextFrom(
        sessionPlan.period.start,
      );
      const financialCheck =
        moment().isBefore(financialClosingMoment) ||
        PrivilegeChecker.hasPrivilege(
          state,
          Privileges.MarkAttendance.IgnoreFinanceDate,
        );
      markable = financialCheck && isActive(group.status);
    }
  } else {
    students = students.filter((member) =>
      sessionPlan.sessionMembers.includes(member.studentGeneralInfo.id),
    );
  }

  students = new List(students);

  return {
    groupNumber: group.number,
    attendanceId,
    markable,
    groupId: ownProps.params.groupId,
    sessionId: ownProps.params.sessionPlanId,
    marking: markable && MarkAttendanceDayView.isInMarkingRoute(ownProps),
    students,
    currentSession,
    isAnyApiCallSaving: !!isAnyApiCallSaving(state),
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(GroupMembers, dispatch),
    bindActionCreators(uiState, dispatch),
    bindActionCreators(SessionsAttendance.actions, dispatch),
  );
}

function isAnyApiCallSaving(state) {
  return !!(state.get("apiCalls") || new List())
    .filter((call, key) =>
      ["PUT", "POST"].some((method) => key.startsWith(method)),
    )
    .find((call) => ApiCall.State.isLoading(call));
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(MarkAttendanceDayView));



// WEBPACK FOOTER //
// ./containers/Group/Attendance/MarkAttendance/MarkAttendanceDayView/component.jsx